import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Typography, CircularProgress, Box,
  TextField, IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { NotificationManager } from "react-notifications";
import {
  GetOrderTemplates, SaveOrderTemplate, UpdateOrderTemplate, DeleteOrderTemplate,
} from "../../../services/ClientsMaintenanceService";

const emptyRow = { orderNumber: "", projectDescription: "" };

const OrderTemplatesDialog = ({ open, onClose, accountNumber }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newRow, setNewRow] = useState(emptyRow);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyRow);

  useEffect(() => {
    if (open && accountNumber) load();
  }, [open, accountNumber]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await GetOrderTemplates(accountNumber);
      setRows(res.results || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    try {
      setLoading(true);
      await SaveOrderTemplate({ accountNumber, orderNumber: newRow.orderNumber.trim(), projectDescription: newRow.projectDescription.trim() });
      setNewRow(emptyRow);
      await load();
    } catch { NotificationManager.error("Failed to add template."); }
    finally { setLoading(false); }
  };

  const handleEditSave = async (row) => {
    try {
      setLoading(true);
      await UpdateOrderTemplate({ recordid: row.recordid, accountNumber, orderNumber: editRow.orderNumber.trim(), projectDescription: editRow.projectDescription.trim() });
      setEditId(null);
      await load();
    } catch { NotificationManager.error("Failed to update template."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (recordid) => {
    try {
      setLoading(true);
      await DeleteOrderTemplate(recordid);
      await load();
    } catch { NotificationManager.error("Failed to delete template."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Order Templates — Account {accountNumber}</DialogTitle>
      <DialogContent dividers>
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Order Number</strong></TableCell>
                    <TableCell><strong>Project Description</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={3}><Typography color="text.secondary" variant="body2">No order templates defined.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((row) =>
                    editId === row.recordid ? (
                      <TableRow key={row.recordid}>
                        <TableCell><TextField size="small" value={editRow.orderNumber} onChange={e => setEditRow(p => ({ ...p, orderNumber: e.target.value }))} fullWidth /></TableCell>
                        <TableCell><TextField size="small" value={editRow.projectDescription} onChange={e => setEditRow(p => ({ ...p, projectDescription: e.target.value }))} fullWidth /></TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => handleEditSave(row)}><SaveIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => setEditId(null)}><CancelIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={row.recordid} hover>
                        <TableCell>{row.orderNumber}</TableCell>
                        <TableCell>{row.projectDescription}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => { setEditId(row.recordid); setEditRow({ orderNumber: row.orderNumber || "", projectDescription: row.projectDescription || "" }); }}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.recordid)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField size="small" label="Order Number" value={newRow.orderNumber} onChange={e => setNewRow(p => ({ ...p, orderNumber: e.target.value }))} sx={{ width: 160 }} />
              <TextField size="small" label="Project Description" value={newRow.projectDescription} onChange={e => setNewRow(p => ({ ...p, projectDescription: e.target.value }))} sx={{ flex: 1 }} />
              <Button variant="contained" size="small" onClick={handleAdd} disabled={loading}>Add</Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderTemplatesDialog;
